import React from 'react';
import scrollToComponent from 'react-scroll-to-component';
import {AnimatePresence} from 'framer-motion';

import { Button, Collapse, Icon, LinearProgressLoader, MultiSelectList, Range, RangeListFilter, symbolUrl} from '../../common';
import dotProp from 'dot-prop-immutable';
import {getSuffix} from '../../helpers/getSuffix';
import ProductOverviewSearch from "./ProductOverviewSearch";

export default function ProductOverviewFilter({
    filters,
    updateFilters,
    filterOpen,
    setFilterOpen,
    isLoading,
    resultsRef,
    query,
    updateQuery,
    resetFilters
}) {
    const updateChoicesFilter = (groupIndex, selectedFilters) => {
        const newFilters = dotProp.set(filters, `${groupIndex}.filters`, selectedFilters);

        updateFilters(newFilters);
    };

    const updateRangeFilter = (groupIndex, filterIndex, value, type) => {
        const newFilters = filters;

        if (type === 'min') {
            newFilters[groupIndex].filters[filterIndex].selectedMinValue = parseInt(value);
        } else if (type === 'max') {
            newFilters[groupIndex].filters[filterIndex].selectedMaxValue = parseInt(value);
        } else if (type === undefined) {
            newFilters[groupIndex].filters[filterIndex].value = value;
        }

        updateFilters(newFilters);
    };

    const updateFilterStatus = () => {
        setFilterOpen(false);

        scrollToComponent(resultsRef.current, {
            offset: -200,
            align: 'top',
            ease: 'inOutCirc',
        });
    };

    const hasActiveFilters = query || filters.some(filterGroup =>
        filterGroup.filters.some(filter => {
            if (filter.viewType === 'multi_select') {
                return filter.choices.some(choice => choice.active);
            }
            if (filter.viewType === 'range') {
                const {selected_min, selected_max} = filter.value || {};
                return typeof selected_min !== 'undefined' || typeof selected_max !== 'undefined';
            }
            if (filter.viewType === 'range_list') {
                const minBound = filter.minValues[0];
                const maxBound = filter.maxValues[filter.maxValues.length - 1];
                return filter.selectedMinValue !== minBound || filter.selectedMaxValue !== maxBound;
            }
            return false;
        })
    );

    return (
        <div className={`card card--filter ${filterOpen && 'is-open'}`}>
            <div className="card__wrapper">
                <div className="card__header d-flex align-items-center justify-content-between d-lg-none">
                    <button
                        className="btn btn-link text-base d-flex align-items-center p-0"
                        type="button"
                        onClick={() => setFilterOpen(!filterOpen)}>
                        <Icon url={symbolUrl('arrow')} size="24" classes="icon--reversed mr-3" />
                        <span className="font-weight-medium">Filteren</span>
                    </button>
                    <div className="d-flex align-items-center">
                        <button
                            className="btn btn-link text-base p-0 mt-1"
                            type="button"
                            onClick={() => setFilterOpen(!filterOpen)}>
                            <Icon url={symbolUrl('cross')} size="14" />
                        </button>
                    </div>
                    <AnimatePresence>{isLoading && <LinearProgressLoader />}</AnimatePresence>
                </div>
                {hasActiveFilters && (
                    <div className="py-4">
                        <button className="btn btn--reset text-white" onClick={resetFilters} type="button">
                            Reset filters
                        </button>
                    </div>
                )}
                <ul className="list-group list-group-flush mb-8 mb-lg-0 mt-4 mt-lg-0">

                    <li className="list-group-item px-0 pt-0 pb-6">
                        <ProductOverviewSearch
                            query={query}
                            updateQuery={updateQuery}
                            filters={filters}
                            updateFilter={(filterGroupIndex, filters) => updateChoicesFilter(filterGroupIndex, filters)}
                        />
                    </li>

                    {filters.map((filterGroup, filterGroupIndex) =>
                        filterGroup.filters.map((filter, filterIndex) => {
                            const suffix = getSuffix(filter.filterKey);

                            if (filter.viewType === 'multi_select' && filter.filterKey === 'sources') {
                                return (
                                    <li
                                        className="list-group-item px-0 py-6"
                                        key={filterGroupIndex}>
                                        {filter.choices.map((choice, choiceIndex) =>
                                            choice.active && (
                                                <input
                                                    key={`hidden-${choiceIndex}`}
                                                    type="hidden"
                                                    name={`${filter.filterKey}[]`}
                                                    value={choice.name}
                                                />
                                            )
                                        )}
                                        <div className="filter-toggle-group">
                                            {filter.choices.map((choice, choiceIndex) => (
                                                <button
                                                    key={choiceIndex}
                                                    className={`filter-toggle ${choice.active ? 'filter-toggle--active' : ''}`}
                                                    type="button"
                                                    onClick={() => {
                                                        const newFilters = dotProp.set(
                                                            filterGroup.filters,
                                                            `${filterIndex}.choices.${choiceIndex}.active`,
                                                            !choice.active
                                                        );
                                                        updateChoicesFilter(filterGroupIndex, newFilters);
                                                    }}
                                                >
                                                    {choice.name.charAt(0).toUpperCase() + choice.name.slice(1)}
                                                </button>
                                            ))}
                                        </div>
                                    </li>
                                );
                            }

                            if (filter.viewType === 'multi_select' && filter.filterKey !== 'source') {
                                return (
                                    <li
                                        className={`list-group-item px-0 py-6`}
                                        key={filterGroupIndex}>
                                        <Collapse
                                            trigger={filter.name}
                                            open={typeof filterGroup.options[0] === 'undefined'}>
                                            <MultiSelectList
                                                className="list-group list-group-flush"
                                                parentFilters={filterGroup.filters}
                                                filter={filter}
                                                groupIndex={filterGroupIndex}
                                                filterIndex={filterIndex}
                                                key={filterIndex}
                                                suffix={suffix}
                                                updateFilters={(filters) => {
                                                    updateChoicesFilter(filterGroupIndex, filters);
                                                }}
                                            />
                                        </Collapse>
                                    </li>
                                );
                            }

                            if (filter.viewType === 'range') {
                                const {name, minBounds, maxBounds, value, filterKey} = filter;
                                const {selected_min, selected_max} = value;

                                const rangeValue =
                                    typeof selected_max !== 'undefined' && typeof selected_min !== 'undefined'
                                        ? {min: selected_min, max: selected_max}
                                        : {min: minBounds, max: maxBounds};

                                let prefix = '';

                                switch (filterKey) {
                                    case 'price':
                                        prefix = '€';
                                        break;
                                }

                                return (
                                    <li className="list-group-item px-0 py-6" key={filterGroupIndex}>
                                        <Collapse trigger={name} open={typeof filterGroup.options[0] === 'undefined'}>
                                            <Range
                                                prefix={prefix}
                                                minBounds={minBounds}
                                                maxBounds={maxBounds}
                                                value={rangeValue}
                                                name={filter.filterKey}
                                                filterName={name}
                                                onChangeComplete={(value) => {
                                                    updateRangeFilter(filterGroupIndex, filterIndex, value, undefined);
                                                }}
                                                thousandSeparator={filter.filterKey !== 'bouwjaar' ? '.' : ''}
                                                decimalSeparator={','}
                                            />
                                        </Collapse>
                                    </li>
                                );
                            }

                            if (filter.viewType === 'range_list') {
                                const {name, selectedMinValue, selectedMaxValue} = filter;

                                return (
                                    <li className="list-group-item px-0 py-6" key={filterGroupIndex}>
                                        <Collapse trigger={name} open={typeof filterGroup.options[0] === 'undefined'}>
                                            <RangeListFilter
                                                layout="row"
                                                filter={filter}
                                                value={{
                                                    min: selectedMinValue,
                                                    max: selectedMaxValue,
                                                }}
                                                updateFilters={(value, type) => {
                                                    updateRangeFilter(filterGroupIndex, filterIndex, value, type);
                                                }}
                                            />
                                        </Collapse>
                                    </li>
                                );
                            }
                        })
                    )}
                </ul>
                <Button
                    classes="btn--fixed-bottom d-lg-none"
                    linkTitle="Filters toepassen"
                    onClick={updateFilterStatus}
                />
            </div>
        </div>
    );
}
